import type { ShotSession, DiagnoseRec, GroupPattern } from './types';

/**
 * Pattern → recommendation matcher.
 *
 * Reads the most recent session's pattern + group size + distance, and
 * returns the most-actionable next-step recommendations. Goal: every
 * range session yields a specific thing the archer should DO next time,
 * not just a number to look at.
 *
 * Logic priority:
 *   1. If group is centered + tight → praise, no fix needed.
 *   2. Otherwise: match the pattern to its primary diagnosis.
 *   3. Add trend-based rec if there's enough history (e.g. "your groups
 *      are 30% wider at 60 than your PB — re-tune").
 *
 * Sources for the diagnosis logic:
 *   - Easton arrow flight bareshaft chart
 *   - John Dudley grip + form material
 *   - Larry Wise tuning compound bows
 *   - PCA sight tape literature
 *   - Kevin Strother's posts on string/cable interaction
 */

export function diagnose(session: ShotSession, history: readonly ShotSession[]): DiagnoseRec[] {
  const recs: DiagnoseRec[] = [];

  // ───── Pattern-driven primary rec ─────
  const patternRec = PATTERN_DIAGNOSES[session.pattern];
  if (patternRec) recs.push(patternRec);

  // ───── Group-size sanity check (rough rule of thumb) ─────
  // Tight = roughly under 1 MOA equivalent (1.047" per 100yd → ~0.42" at 40yd).
  // Loose = > 3 MOA at distance suggests fundamentals not dialed.
  const moa = (session.groupSizeIn / session.distanceYd) * 100; // ~MOA-ish
  if (moa > 4 && session.pattern !== 'scattered') {
    recs.push({
      title: 'Group is wider than 4 MOA — start at form',
      why: 'At this distance the group is opening up beyond what equipment alone explains. Form variables (grip, anchor, release) dominate at >4 MOA.',
      fix: 'Before chasing equipment: video your draw + release at full speed and at slow-mo. Look for the bow hand jumping at release (grip torque), inconsistent anchor, or punch-trigger.',
      goToTuneStep: 'bow_grip',
      severity: 'warn',
    });
  }

  // ───── Trend-based rec ─────
  // If we have 3+ prior sessions at the SAME distance, compare to PB.
  const sameDist = history.filter(
    (h) => h.id !== session.id && h.distanceYd === session.distanceYd,
  );
  if (sameDist.length >= 3) {
    const best = Math.min(...sameDist.map((h) => h.groupSizeIn));
    const drift = ((session.groupSizeIn - best) / best) * 100;
    if (drift > 40 && session.pattern !== 'centered') {
      recs.push({
        title: `Group is ${Math.round(drift)}% wider than your best at ${session.distanceYd}yd`,
        why: `Your best at ${session.distanceYd}yd is ${best.toFixed(1)}". Today was ${session.groupSizeIn.toFixed(1)}". That's a meaningful regression — bow may have drifted out of tune.`,
        fix: 'Run a quick paper-tune check + verify cam timing. New strings can creep ~50 arrows in; cables stretch over months. Worth a 15-min wizard pass.',
        goToTuneStep: 'paper_tune',
        severity: 'warn',
      });
    }
  }

  // ───── If perfectly centered + tight, celebrate ─────
  if (session.pattern === 'centered' && moa < 2) {
    recs.push({
      title: 'Locked in. Dialed tune, repeatable form.',
      why: `${session.groupSizeIn.toFixed(1)}" at ${session.distanceYd}yd = ~${moa.toFixed(1)} MOA. Equipment + form are aligned.`,
      fix: 'Push your distance. Move to next-longer yardage and bank a PB. If this is your longest distance, drop arrow count from 6 to 3 per group and track consistency over more sessions.',
      severity: 'info',
    });
  }

  return recs;
}

/**
 * Per-pattern diagnoses. Each one is the PRIMARY rec when the user picks
 * that pattern in the log form. Order matters — these run first before any
 * trend-based recs.
 */
const PATTERN_DIAGNOSES: Record<GroupPattern, DiagnoseRec | null> = {
  'centered': null, // handled in main diagnose() based on group size

  'left-string': {
    title: 'Stringing LEFT — three things to check, in order',
    why: 'Consistent left bias (RH shooter) is almost always one of: grip torque pushing the bow at release, cam lean tipped right, or rest sitting LEFT of center shot spec.',
    fix: '1. Check grip first — thumb at 45° on the lifeline, fingers loose. 2. Verify cam pre-lean (top + bottom cams aligned at brace). 3. Last: measure center shot vs your bow\'s spec sheet. Skip ahead to grip assessment in the Tune wizard — that\'s the fastest fix and the most common cause.',
    goToTuneStep: 'bow_grip',
    severity: 'warn',
  },

  'right-string': {
    title: 'Stringing RIGHT — three things to check, in order',
    why: 'Consistent right bias (RH shooter) usually means rest sitting RIGHT of center shot, spine too weak for the bow + point combo, or cam lean tipped left.',
    fix: '1. Move the rest 1/32" LEFT and re-shoot. 2. Check spine — is your shaft right for your effective draw weight + point weight? 3. If still right after rest + spine: yoke-tune cam lean. Bareshaft test (next Tune step) will quickly confirm whether spine is the issue.',
    goToTuneStep: 'bareshaft',
    severity: 'warn',
  },

  'high': {
    title: 'Stringing HIGH — nock or rest height',
    why: 'High groups are nock-point too high or rest too low. Easier fix than horizontal — usually 1/32" of nock-point adjustment cleans it up.',
    fix: 'Drop your nocking point 1/32" DOWN the string, re-shoot 6 arrows. If still high, raise the rest 1/32" instead. Paper tune step confirms which one.',
    goToTuneStep: 'nocking_d_loop',
    severity: 'info',
  },

  'low': {
    title: 'Stringing LOW — nock or rest height (less common)',
    why: 'Low groups are nock too low or rest too high. Less common than high — often a sign someone over-corrected an earlier high group.',
    fix: 'Move nocking point 1/32" UP the string. If still low, lower the rest. Re-check tiller while you\'re there — positive tiller (top tighter) can also cause persistent low tears.',
    goToTuneStep: 'nocking_d_loop',
    severity: 'info',
  },

  'vertical': {
    title: 'Vertical string — spine inconsistency or rest contact',
    why: 'Arrows landing at the same X but spread up-down means each arrow is leaving the bow with different vertical kinetics. Two suspects: spine variance across your arrows, or vanes touching the rest on the way out.',
    fix: '1. Spray rest + cables with foot powder, shoot one arrow, look for fletching streaks. 2. If no contact: bareshaft test — if bareshafts hit same hole as fletched, your arrows differ in spine. Cull the inconsistent ones.',
    goToTuneStep: 'bareshaft',
    severity: 'warn',
  },

  'horizontal': {
    title: 'Horizontal string — form, grip, or release torque',
    why: 'Same Y, varying X = lateral inconsistency at release. Equipment doesn\'t cause this — your hand or release does.',
    fix: 'Three things: 1) Grip — thumb at 45°, fingers loose, NO white knuckles. 2) Anchor — same spot every time, jawbone or peep-to-tip-of-nose. 3) Release — back tension, not punch-trigger. Video your draw cycle from behind.',
    goToTuneStep: 'bow_grip',
    severity: 'warn',
  },

  'scattered': {
    title: 'Scattered group — start here',
    why: 'No discernible pattern usually means fundamentals aren\'t locked in yet. Equipment fixes won\'t help until form repeats shot-to-shot.',
    fix: 'Shoot 10 arrows at 20 yards with one focus per group of 3-5: grip, anchor, release. Don\'t worry about the target — worry about your sensations being identical shot to shot. Once your 20-yard groups are 3" or less, move out.',
    goToTuneStep: 'bow_grip',
    severity: 'critical',
  },

  'diagonal': {
    title: 'Diagonal string — fix vertical first, horizontal second',
    why: 'Diagonal = combination of vertical (spine/nock height) + horizontal (form/grip). Fixing them together is whack-a-mole. Always isolate vertical first.',
    fix: '1. Check nock height — 1/32" at a time. 2. Spray-test for vane contact. 3. Once vertical is clean, the diagonal will collapse into either left or right string, which is then a known fix (see grip / center shot).',
    goToTuneStep: 'paper_tune',
    severity: 'warn',
  },
};
